import { Nota, Tabela, Tag, Td } from '../../shared/ui/primitivas';
import { TelaAnery } from '../../shared/ui/TelaAnery';
import type { AneryChip, AneryTag } from '../../shared/ui/tiposDaEsteira';

/**
 * Portada do protótipo, onde a tela se chama "Cases" — aqui não, porque no
 * glossário `case` é a PESSOA que conduz o paciente. A tela é a conduta; a
 * case é para quem ela é roteada, e é o que a coluna "núcleo / case" diz.
 *
 * Fronteira da fonte: quando existir `GET /condutas`, o fixture vira consulta
 * aqui dentro e a tela não muda.
 */
const CONDUTA_MEDICA = {
  breadcrumb: 'operação / conduta médica / roteamento',
  titulo: 'Conduta médica — roteada por regra',
  subtitulo:
    'A conduta do médico chega numa lista de e-mail de toda a Anery, sem filtro por núcleo. Aqui a regra identifica o paciente, roteia para a case certa e avisa por evento — não por telefonema no meio da rotina.',
  chips: [
    { label: 'roteamento por núcleo' },
    { label: 'homônimo vai para revisão' },
    { label: 'notificação por evento' },
  ] as AneryChip[],
  linhas: [
    {
      paciente: 'Pac. 0482',
      conduta: 'Ajuste de dieta enteral',
      nucleo: 'SP-1 · Ana',
      urgencia: { label: 'alta', tone: 'block' },
      status: { label: 'roteado', tone: 'info' },
    },
    {
      paciente: 'Pac. 0916',
      conduta: 'Alteração de frequência',
      nucleo: 'SP-2 · Carla',
      urgencia: { label: 'média', tone: 'warn' },
      status: { label: 'case ciente', tone: 'ok' },
    },
    {
      paciente: 'Pac. 1177',
      conduta: 'Nova prescrição pós-alta',
      nucleo: 'RJ · Marcos',
      urgencia: { label: 'alta', tone: 'block' },
      status: { label: 'em análise', tone: 'warn' },
    },
    {
      paciente: 'Pac. 0233',
      conduta: 'Troca de equipamento',
      nucleo: 'BH · Paula',
      urgencia: { label: 'média', tone: 'warn' },
      status: { label: 'técnico a caminho', tone: 'info' },
    },
    {
      paciente: 'Pac. 0655',
      conduta: 'Orientação à família',
      nucleo: 'SP-1 · Ana',
      urgencia: { label: 'baixa', tone: 'neutral' },
      status: { label: 'resolvido', tone: 'ok' },
    },
    {
      paciente: '',
      pacienteTag: { label: '2 homônimos', tone: 'info' },
      conduta: 'Nome não resolveu para um id do IW',
      nucleo: '—',
      urgencia: { label: 'média', tone: 'warn' },
      status: { label: 'fila de revisão', tone: 'info' },
    },
  ] as {
    paciente: string;
    pacienteTag?: AneryTag;
    conduta: string;
    nucleo: string;
    urgencia: AneryTag;
    status: AneryTag;
  }[],
  nota: 'Abrir e baixar a ocorrência no IW depende do marco zero das 3.932 ocorrências e da API de escrita. Até lá o roteamento e a fila funcionam só com leitura, e a ocorrência é replicada à mão.',
};

export function PaginaDeCondutaMedica(): React.JSX.Element {
  return (
    <TelaAnery
      breadcrumb={CONDUTA_MEDICA.breadcrumb}
      titulo={CONDUTA_MEDICA.titulo}
      subtitulo={CONDUTA_MEDICA.subtitulo}
      chips={CONDUTA_MEDICA.chips}
    >
      <Tabela
        colunas={[
          { rot: 'Paciente' },
          { rot: 'Conduta' },
          { rot: 'Núcleo / case' },
          { rot: 'Urgência' },
          { rot: 'Status' },
        ]}
      >
        {CONDUTA_MEDICA.linhas.map((l, i) => (
          <tr key={l.paciente || `homonimos-${i}`}>
            {/* Identificador de paciente é código, não nome: mono, para que
                dois deles se comparem caractere a caractere. */}
            <Td className="num">{l.pacienteTag ? <Tag {...l.pacienteTag} /> : l.paciente}</Td>
            <Td>{l.conduta}</Td>
            <Td fraco>{l.nucleo}</Td>
            <Td>
              <Tag {...l.urgencia} />
            </Td>
            <Td>
              <Tag {...l.status} />
            </Td>
          </tr>
        ))}
      </Tabela>

      <Nota titulo="Dependência aberta.">{CONDUTA_MEDICA.nota}</Nota>
    </TelaAnery>
  );
}
